import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { SpotImage } from "@/components/OfflineSpotsSection";
import { ArrowLeft, MapPin, Trash, ChatCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";
import { track } from "@/lib/track";
import { usePageMeta, snippet } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAIL = "swarajrp030@gmail.com";
const MAX_COMMENT = 1000;

const mono = { fontFamily: "IBM Plex Mono, monospace" };

const timeAgo = (iso) => {
  const secs = Math.floor((Date.now() - Date.parse(iso)) / 1000);
  if (Number.isNaN(secs)) return "";
  const steps = [[31536000,"y"],[2592000,"mo"],[604800,"w"],[86400,"d"],[3600,"h"],[60,"m"]];
  for (const [n, label] of steps) if (secs >= n) return `${Math.floor(secs / n)}${label} ago`;
  return "just now";
};

const Avatar = ({ name, picture, size = 28 }) =>
  picture ? (
    <img src={picture} alt="" className="rounded-full object-cover shrink-0"
      style={{ width: size, height: size }} />
  ) : (
    <span
      className="rounded-full bg-[#F0EFEB] text-[#6B6A68] flex items-center justify-center shrink-0 text-[11px]"
      style={{ width: size, height: size, ...mono }}
    >
      {(name || "?").slice(0, 1).toUpperCase()}
    </span>
  );

// Detail view for one offline spot: the full record plus its comment threads.
// Replies are one level deep, matching the API — a spot needs a conversation,
// not a tree.
const OfflineSpotDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [spot, setSpot] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [user, setUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null); // comment id being replied to
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data)).catch(() => setUser(null));
    axios.get(`${API}/categories`).then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const loadComments = useCallback(() => {
    axios.get(`${API}/offline-spots/${id}/comments`)
      .then((r) => setThreads(r.data?.items || []))
      .catch(() => setThreads([]));
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/offline-spots/${id}`)
      .then((r) => {
        if (cancelled) return;
        setSpot(r.data);
        track("offline_spot_view", { spot_id: id, city: r.data?.city });
      })
      .catch((e) => { if (!cancelled) setNotFound(e?.response?.status === 404); })
      .finally(() => !cancelled && setLoading(false));
    loadComments();
    return () => { cancelled = true; };
  }, [id, loadComments]);

  const post = async (text, parentId) => {
    if (!user) {
      track("offline_spot_comment_blocked_signed_out", { spot_id: id });
      toast.error("Please sign in to comment");
      navigate("/login");
      return false;
    }
    const clean = (text || "").trim();
    if (!clean) return false;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("body", clean);
      if (parentId) fd.append("parent_id", parentId);
      await axios.post(`${API}/offline-spots/${id}/comments`, fd, { withCredentials: true });
      track(parentId ? "offline_spot_reply" : "offline_spot_comment", {
        spot_id: id,
        is_own_spot: user?.user_id === spot?.user_id,
      });
      loadComments();
      setSpot((s) => (s ? { ...s, comment_count: (s.comment_count || 0) + 1 } : s));
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post that");
      return false;
    } finally {
      setSending(false);
    }
  };

  const submitTop = async (e) => {
    e.preventDefault();
    if (await post(body)) setBody("");
  };

  const submitReply = async (e, parentId) => {
    e.preventDefault();
    if (await post(replyBody, parentId)) {
      setReplyBody("");
      setReplyTo(null);
    }
  };

  const canDelete = (c) => user && (user.user_id === c.user_id || user.email === ADMIN_EMAIL);

  const removeComment = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(`${API}/offline-spots/comments/${c.id}`, { withCredentials: true });
      track("offline_spot_comment_delete", { spot_id: id });
      loadComments();
    } catch {
      toast.error("Could not delete that comment");
    }
  };

  // Hooks run before the early returns below, so this reads spot defensively.
  usePageMeta(
    spot ? `${spot.store_name}, ${spot.city} — ${spot.kind === "shoes" ? "shoes" : "clothes"} for tall people` : null,
    spot
      ? snippet(
          `${spot.store_name}${spot.area ? ` in ${spot.area}` : ""}, ${spot.city}: a store where tall people found ` +
            `${spot.kind === "shoes" ? "shoes" : "clothes"} that fit${spot.notes ? ` — ${spot.notes}` : "."}`
        )
      : null
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F5] px-6 py-16">
        <div className="max-w-2xl mx-auto space-y-4">
          <div className="h-8 w-2/3 bg-[#F0EFEB] rounded animate-pulse" />
          <div className="h-40 bg-[#F0EFEB] rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (notFound || !spot) {
    return (
      <div className="min-h-screen bg-[#FAF9F5] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-lg text-[#6B6A68] mb-4">This spot isn't here anymore.</p>
          <button onClick={() => navigate("/offline-spots")}
            className="text-xs uppercase tracking-[0.2em] underline" style={mono}>
            Back to spots
          </button>
        </div>
      </div>
    );
  }

  const cat = categories.find((c) => c.id === spot.category_id);
  const unit = (cat?.size_label || (spot.kind === "shoes" ? "UK" : "ft")).trim();
  const sizes = (spot.sizes || []).join(", ");
  const place = [spot.area, spot.city].filter(Boolean).join(", ");

  const CommentBox = ({ value, onChange, onSubmit, placeholder, testId, autoFocus }) => (
    <form onSubmit={onSubmit} className="flex items-start gap-2">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, MAX_COMMENT))}
        rows={2}
        autoFocus={autoFocus}
        placeholder={placeholder}
        className="flex-1 px-3 py-2 bg-white border border-[#E4E1D9] rounded-md text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#2D302A]"
        data-testid={testId}
      />
      <button
        type="submit"
        disabled={sending || !value.trim()}
        className="shrink-0 bg-[#2D302A] text-white p-2.5 rounded-md hover:bg-[#1A1C18] transition-colors disabled:opacity-40"
        aria-label="Post"
        data-testid={`${testId}-submit`}
      >
        <PaperPlaneTilt size={16} weight="fill" />
      </button>
    </form>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <Toaster position="top-center" richColors />
      <header className="border-b border-[#E4E1D9] px-6 md:px-12 py-4 flex items-center gap-4">
        <button type="button" onClick={() => navigate("/offline-spots")}
          className="flex items-center gap-2 text-[#6B6A68] hover:text-[#1A1A1A] transition-colors text-xs uppercase tracking-[0.2em]"
          style={mono} data-testid="spot-detail-back">
          <ArrowLeft size={16} /> Back
        </button>
        <h1 className="text-2xl md:text-3xl tracking-tighter ml-2"
          style={{ fontFamily: "Cormorant Garamond, serif" }}>
          North of Six Club
        </h1>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 md:py-14">
        <SpotImage spot={spot} glyphSize={48}
          className="rounded-md border border-[#E4E1D9] mb-6" />

        <p className="text-[10px] uppercase tracking-[0.3em] text-[#6B6A68] mb-2" style={mono}>
          Offline spot
        </p>
        <h2 className="text-4xl md:text-5xl tracking-tighter mb-3"
          style={{ fontFamily: "Cormorant Garamond, serif" }} data-testid="spot-detail-name">
          {spot.store_name}
        </h2>

        {place && (
          <p className="flex items-start gap-2 text-sm text-[#6B6A68] mb-4" data-testid="spot-detail-place">
            <MapPin size={16} className="mt-[2px] shrink-0" /> {place}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {sizes && (
            <span className="text-[11px] uppercase tracking-[0.12em] bg-[#F0EFEB] px-2.5 py-1.5 rounded-sm"
              style={mono} data-testid="spot-detail-size">
              {spot.kind === "shoes" ? `${unit} ${sizes}` : `${sizes} ${unit}`}
            </span>
          )}
          {cat && (
            <span className="text-[11px] uppercase tracking-[0.12em] border border-[#E4E1D9] px-2.5 py-1.5 rounded-sm text-[#6B6A68]" style={mono}>
              {cat.name}
            </span>
          )}
        </div>

        {spot.notes && (
          <p className="text-[15px] leading-relaxed text-[#2D302A] mb-6" data-testid="spot-detail-notes">
            {spot.notes}
          </p>
        )}

        <div className="flex items-center gap-2 pb-8 border-b border-[#E4E1D9]">
          <Avatar name={spot.user_name} picture={spot.user_picture} />
          <p className="text-[11px] text-[#9A9A98]" style={mono}>
            shared by {spot.user_name || "someone"} · {timeAgo(spot.created_at)}
          </p>
        </div>

        {/* Comments */}
        <section className="pt-8" data-testid="spot-comments">
          <h3 className="flex items-center gap-2 text-2xl tracking-tighter mb-5"
            style={{ fontFamily: "Cormorant Garamond, serif" }}>
            <ChatCircle size={20} />
            {spot.comment_count || 0} {spot.comment_count === 1 ? "comment" : "comments"}
          </h3>

          <div className="mb-8">
            <CommentBox
              value={body}
              onChange={setBody}
              onSubmit={submitTop}
              placeholder={user ? "Ask something about this spot…" : "Sign in to comment…"}
              testId="spot-comment-input"
            />
          </div>

          {threads.length === 0 ? (
            <p className="text-sm text-[#9A9A98]">No comments yet — ask the first question.</p>
          ) : (
            <ul className="space-y-6">
              {threads.map((c) => (
                <li key={c.id} data-testid="spot-comment-thread">
                  <div className="flex items-start gap-3">
                    <Avatar name={c.user_name} picture={c.user_picture} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-[#9A9A98] mb-1" style={mono}>
                        {c.user_name || "someone"}
                        {c.user_id === spot.user_id && (
                          <span className="ml-2 text-[#2D302A]">· shared this spot</span>
                        )}
                        {" · "}{timeAgo(c.created_at)}
                      </p>
                      <p className="text-sm text-[#2D302A] whitespace-pre-wrap break-words">{c.body}</p>
                      <div className="flex items-center gap-4 mt-1.5">
                        <button type="button"
                          onClick={() => { setReplyTo(replyTo === c.id ? null : c.id); setReplyBody(""); }}
                          className="text-[11px] text-[#6B6A68] hover:underline" style={mono}
                          data-testid="spot-comment-reply">
                          Reply
                        </button>
                        {canDelete(c) && (
                          <button type="button" onClick={() => removeComment(c)}
                            className="text-[11px] text-[#B23A48] hover:underline flex items-center gap-1" style={mono}>
                            <Trash size={11} /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Replies — one level, indented under their parent */}
                  {(c.replies?.length > 0 || replyTo === c.id) && (
                    <ul className="mt-4 ml-5 pl-5 border-l border-[#E4E1D9] space-y-4">
                      {(c.replies || []).map((r) => (
                        <li key={r.id} className="flex items-start gap-3" data-testid="spot-comment-reply-item">
                          <Avatar name={r.user_name} picture={r.user_picture} size={24} />
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] text-[#9A9A98] mb-1" style={mono}>
                              {r.user_name || "someone"}
                              {r.user_id === spot.user_id && (
                                <span className="ml-2 text-[#2D302A]">· shared this spot</span>
                              )}
                              {" · "}{timeAgo(r.created_at)}
                            </p>
                            <p className="text-sm text-[#2D302A] whitespace-pre-wrap break-words">{r.body}</p>
                            {canDelete(r) && (
                              <button type="button" onClick={() => removeComment(r)}
                                className="mt-1.5 text-[11px] text-[#B23A48] hover:underline flex items-center gap-1" style={mono}>
                                <Trash size={11} /> Delete
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                      {replyTo === c.id && (
                        <li>
                          <CommentBox
                            value={replyBody}
                            onChange={setReplyBody}
                            onSubmit={(e) => submitReply(e, c.id)}
                            placeholder={`Reply to ${c.user_name || "this comment"}…`}
                            testId="spot-reply-input"
                            autoFocus
                          />
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
};

export default OfflineSpotDetail;
